// Shared Assessment/Plan note helpers (2026-08-20).
//
// Different pipelines (api2/dallas legacy, some LLM template runs) store the
// ENTIRE assessment/plan as one HTML blob with every diagnosis embedded as a
// "Title (ICD):" heading. Every renderer that shows one card/line per array
// entry then collapses the whole A&P into a single card. These helpers give
// all surfaces (edit screen, chart sidebar, visit modal) identical
// shape-normalization: HTML → text with line breaks, blob → one entry per
// diagnosis heading. Well-formed entries pass through unchanged.

// Some pipelines emit LITERAL backslash-n sequences inside the text (the two
// characters \ and n, not a newline) — convert them to real breaks first or
// they render verbatim and defeat the per-diagnosis splitter (QA 2026-08-20,
// rec 18281).
const literalEscapesToBreaks = (s) =>
  typeof s === "string" ? s.replace(/\\r\\n|\\n/g, "\n").replace(/\\t/g, "  ") : s;

export const htmlNoteToText = (input) => {
  const s = literalEscapesToBreaks(input);
  if (typeof s !== "string" || !/<[a-z][^>]*>/i.test(s)) return s;
  const withBreaks = s
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<li[^>]*>/gi, "- ");
  const el = document.createElement("div");
  el.innerHTML = withBreaks;
  return (el.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

export const AP_ICD_HEADING = /^[^:\n]{2,90}\(([A-Z][0-9][0-9A-Z.]{0,6})\)\s*:\s*$/;
// Inline variant: "Title (ICD): body starts on the same line" — each <p> of
// some pipelines is one such line (rec 18278).
export const AP_ICD_INLINE = /^[^:\n]{2,90}\(([A-Z][0-9][0-9A-Z.]{0,6})\)\s*:\s+\S/;
const AP_BARE_HEADING = /^[A-Za-z][^:\n]{1,40}:\s*$/;

export const splitApEntry = (raw) => {
  if (typeof raw !== "string") return [raw];
  const txt = literalEscapesToBreaks(raw);
  const lines = txt.split("\n");
  const isIcdHeading = (ln) => AP_ICD_HEADING.test(ln.trim()) || AP_ICD_INLINE.test(ln.trim());
  const icdHeadingCount = lines.filter(isIcdHeading).length;
  if (icdHeadingCount < 2) return [txt];
  const isHeading = (ln) => isIcdHeading(ln) || AP_BARE_HEADING.test(ln.trim());
  const out = [];
  let cur = [];
  for (const ln of lines) {
    if (isHeading(ln) && cur.some((x) => x.trim())) {
      out.push(cur.join("\n").trim());
      cur = [];
    }
    cur.push(ln);
  }
  if (cur.some((x) => x.trim())) out.push(cur.join("\n").trim());
  return out.filter(Boolean);
};

// Normalize a whole assessment_plan array: HTML → text, blobs → per-diagnosis.
export const splitApEntries = (arr) =>
  (Array.isArray(arr) ? arr : [arr]).flatMap((x) =>
    typeof x === "string" ? splitApEntry(htmlNoteToText(x)) : [x],
  );

// Parse one entry into {title, icd, body} for card renderers. Falls back to
// {title:null, body} when the entry has no recognizable heading line.
export const parseApEntry = (txt) => {
  if (typeof txt !== "string") return { title: null, icd: null, body: String(txt ?? "") };
  const lines = txt.split("\n");
  const first = (lines[0] || "").trim();
  const icdMatch = first.match(AP_ICD_HEADING);
  if (icdMatch) {
    return {
      title: first.replace(/\([A-Z][0-9][0-9A-Z.]{0,6}\)\s*:\s*$/, "").trim(),
      icd: icdMatch[1],
      body: lines.slice(1).join("\n").trim(),
    };
  }
  // "Title (ICD): body on same line" variant
  const inline = first.match(/^([^:\n]{2,90})\(([A-Z][0-9][0-9A-Z.]{0,6})\)\s*:\s*(.+)$/);
  if (inline) {
    return {
      title: inline[1].trim(),
      icd: inline[2],
      body: [inline[3], ...lines.slice(1)].join("\n").trim(),
    };
  }
  if (AP_BARE_HEADING.test(first)) {
    return { title: first.replace(/:\s*$/, ""), icd: null, body: lines.slice(1).join("\n").trim() };
  }
  return { title: null, icd: null, body: txt };
};
